import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Image as ImageIcon, Trash2, Plus, Loader2 } from 'lucide-react';
import { api, apiUpload } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';

interface DocRow {
  id: string;
  documentName: string | null;
  type: string | null;
  fileKey: string | null;
  imageKey: string | null;
  fileUpload: string | null;
  image: string | null;
}

const TYPE_OPTIONS = [
  { value: 'Document', label: 'Document' },
  { value: 'Photo',    label: 'Photo' },
];

/**
 * Per-job-card document & photo manager. Persists to the same
 * `/service-history-documents` table the Service History detail view uses
 * (filtered by `serviceHistory`), so anything the technician uploads here
 * is visible on the downstream service record without a second sync step.
 */
export function JobCardDocuments({
  serviceHistoryId,
  readOnly = false,
}: {
  serviceHistoryId: string;
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const queryKey = ['service-history-documents', serviceHistoryId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await api<DocRow[]>(`/service-history-documents?serviceHistory=${serviceHistoryId}&limit=100`);
      return r.data ?? [];
    },
    enabled: !!serviceHistoryId,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/service-history-documents/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Delete failed');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast('Removed', undefined, 'success');
    },
    onError: (e: any) => toast('Error', e?.message ?? String(e), 'error'),
  });

  const rows = data ?? [];

  return (
    <div className="space-y-3">
      {isLoading && (
        <div className="text-sm text-muted-foreground py-2">Loading documents…</div>
      )}

      {!isLoading && rows.length === 0 && (
        <div className="text-sm text-muted-foreground py-2">No documents uploaded yet.</div>
      )}

      {rows.map((row) => (
        <DocumentRow key={row.id} row={row} readOnly={readOnly} onRemove={() => remove.mutate(row.id)} />
      ))}

      {!readOnly && (
        <AddDocumentForm
          serviceHistoryId={serviceHistoryId}
          onAdded={() => qc.invalidateQueries({ queryKey })}
        />
      )}
    </div>
  );
}

function DocumentRow({
  row, readOnly, onRemove,
}: { row: DocRow; readOnly: boolean; onRemove: () => void }) {
  const isPhoto = row.type === 'Photo' || (!!row.imageKey && !row.fileKey);
  const key = row.imageKey ?? row.fileKey;
  const filename = key ? key.split('/').pop()!.replace(/^\d+-/, '') : (row.documentName || 'Untitled');

  const { data: urlData } = useQuery({
    queryKey: ['upload-url', key],
    queryFn: async () => {
      if (!key) return null;
      const r = await api<{ key: string; url: string }>(`/uploads/${encodeURIComponent(key)}/url`);
      return r.data ?? null;
    },
    enabled: !!key,
    staleTime: 30 * 60 * 1000,
  });
  const url = urlData?.url ?? null;

  return (
    <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
      {isPhoto && url ? (
        <img src={url} alt={filename} className="h-16 w-16 object-cover rounded border shrink-0" />
      ) : (
        <div className="h-16 w-16 rounded border flex items-center justify-center bg-background shrink-0">
          {isPhoto ? <ImageIcon className="h-6 w-6 text-muted-foreground" /> : <FileText className="h-6 w-6 text-muted-foreground" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate" title={row.documentName || filename}>
          {row.documentName || filename}
        </div>
        <div className="text-xs text-muted-foreground">{row.type || (isPhoto ? 'Photo' : 'Document')}</div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
            Download / open
          </a>
        )}
      </div>
      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-destructive hover:text-destructive/80"
          aria-label="Remove document"
          title="Remove"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function AddDocumentForm({
  serviceHistoryId, onAdded,
}: { serviceHistoryId: string; onAdded: () => void }) {
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState<'Document' | 'Photo'>('Document');
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function handleFile(file: File) {
    setBusy(true);
    try {
      // 1. Upload binary → server returns an S3 key.
      const fd = new FormData();
      fd.append('prefix', 'service-history-documents');
      fd.append('file', file);
      const up = await apiUpload<{ key: string; url: string }>('/uploads', fd);
      if (!up.success || !up.data?.key) throw new Error(up.error?.message || 'Upload failed');

      // 2. Persist a metadata row pointing at the uploaded key. `type` drives
      //    which column (imageKey vs fileKey) the file lands in so the row
      //    renders correctly on both the Service History detail view and this
      //    component's list.
      const body: Record<string, unknown> = {
        serviceHistory: serviceHistoryId,
        documentName: name.trim() || file.name,
        type,
      };
      if (type === 'Photo') body.imageKey = up.data.key;
      else body.fileKey = up.data.key;

      const created = await api('/service-history-documents', { method: 'POST', body: JSON.stringify(body) });
      if (!created.success) throw new Error(created.error?.message || 'Save failed');

      setName('');
      onAdded();
      toast('Uploaded', undefined, 'success');
    } catch (e: any) {
      toast('Error', e?.message ?? String(e), 'error');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="border-t pt-3 mt-2 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-2">
        <div>
          <Label className="text-xs">Document name (optional)</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Defaults to the filename"
            className="h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={type} onValueChange={(v) => setType(v as 'Document' | 'Photo')}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={type === 'Photo' ? 'image/*' : undefined}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
        {busy ? 'Uploading…' : `Add ${type}`}
      </Button>
    </div>
  );
}
